import { useState, useCallback, useEffect } from 'react'
import { Title, Text, Stack, Divider, Center, Skeleton, Paper, Group, Space } from 'components'
import { useTranslation } from 'react-i18next'
import { useDisclosure } from 'hooks'
import { notifications } from 'context'
import { IconCheck, IconX } from '@tabler/icons-react'
import { AddedSkillsList, AddSkillsModal, ConfirmRemovalModal } from './components'
import { useProjectForm } from '../../hooks/useProjectForm'
import { useArcStepper } from '../../context/ArcStepperContext'
import { SelectedSkillStandard } from '../../types'
import { ISkillsStandards } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { updateSelectedSkillsStandards } from 'pages/NewProject/service/SkillsStandardsService'
import { getCurriculumItemsByGrade } from '../../services/CurriculumService'
import { checkItemRemoval, removeProjectItem, extractErrorMessage } from '../../services/ProjectItemsService'
import {
  addSkillsStandards,
  removeSkillStandard,
  extractItemIds
} from '../../utils/projectItems.transformers'

interface ConfirmModalState {
  opened: boolean
  itemId: string
  message: string
}

export const StandardsAndSkillsStep = () => {
  const { t } = useTranslation()
  const { formData, updateField } = useProjectForm()
  const { projectId } = useArcStepper()
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)

  const [skillsStandards, setSkillsStandards] = useState<ISkillsStandards | null>(null)
  const [isLoadingCurriculum, setIsLoadingCurriculum] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    opened: false,
    itemId: '',
    message: ''
  })
  const [isRemoving, setIsRemoving] = useState(false)
  const [isChecking, setIsChecking] = useState(false)

  // ✅ Load curriculum from Arc Mosaico API (always uses /api/arc_mosaico/curriculum_items?grade_id=X)
  useEffect(() => {
    const loadCurriculum = async () => {
      // Arc flow: ALWAYS use grade_id to fetch curriculum
      const gradeId = formData.gradeLevel

      if (!gradeId) {
        // No grade - stop loading and show error state
        console.warn('[StandardsAndSkillsStep] No gradeLevel available')
        setIsLoadingCurriculum(false)
        setSkillsStandards({
          byCycle: false,
          groupSchoolTerm: { name: '' },
          totalStudents: 0,
          selectedItems: { contents: [], specificSkills: [] },
          curriculumItems: { subjects: [], axes: [] },
        })
        return
      }

      try {
        setIsLoadingCurriculum(true)
        console.log('[StandardsAndSkillsStep] Loading curriculum for grade:', gradeId)
        const data = await getCurriculumItemsByGrade(gradeId)
        console.log('[StandardsAndSkillsStep] Curriculum loaded:', data)

        // Validate data structure
        if (data && data.curriculumItems) {
          setSkillsStandards(data)
        } else {
          console.warn('[StandardsAndSkillsStep] Invalid curriculum data structure')
          // Set empty curriculum to avoid errors
          setSkillsStandards({
            byCycle: false,
            groupSchoolTerm: { name: '' },
            totalStudents: 0,
            selectedItems: { contents: [], specificSkills: [] },
            curriculumItems: { subjects: [], axes: [] },
          })
        }
      } catch (error) {
        console.error('Error loading curriculum by grade:', error)
        notifications.show({
          message: t('arc_error_loading_curriculum'),
          color: 'red',
          icon: <IconX size={18} />
        })
        // Set empty curriculum on error
        setSkillsStandards({
          byCycle: false,
          groupSchoolTerm: { name: '' },
          totalStudents: 0,
          selectedItems: { contents: [], specificSkills: [] },
          curriculumItems: { subjects: [], axes: [] },
        })
      } finally {
        setIsLoadingCurriculum(false)
      }
    }

    loadCurriculum()
  }, [formData.gradeLevel, t])

  const executeCurrentRemovalFlow = useCallback(async (id: string) => {
    const newItems = removeSkillStandard(formData.skillsAndStandards, id)
    updateField('skillsAndStandards', newItems)

    try {
      const itemIds = extractItemIds(newItems)
      const response = await updateSelectedSkillsStandards(projectId!, { lv4_item_ids: itemIds })

      if (response?.errors && response.errors !== '') {
        updateField('skillsAndStandards', formData.skillsAndStandards)
        notifications.show({
          message: response.errors,
          color: 'red',
          icon: <IconX size={18} />
        })
        return
      }

      notifications.show({
        message: t('arc_item_removed'),
        color: 'green',
        icon: <IconCheck size={18} />
      })
    } catch (error: unknown) {
      updateField('skillsAndStandards', formData.skillsAndStandards)
      notifications.show({
        message: extractErrorMessage(error, t('arc_error_removing_item')),
        color: 'red',
        icon: <IconX size={18} />
      })
    }
  }, [projectId, formData.skillsAndStandards, updateField, t])

  const executeConfirmedRemoval = useCallback(async (id: string) => {
    if (!projectId) return

    setIsRemoving(true)
    try {
      await removeProjectItem(projectId, id)
      const newItems = removeSkillStandard(formData.skillsAndStandards, id)
      updateField('skillsAndStandards', newItems)
      notifications.show({
        message: t('arc_item_removed'),
        color: 'green',
        icon: <IconCheck size={18} />
      })
      setConfirmModal({ opened: false, itemId: '', message: '' })
    } catch (error: unknown) {
      notifications.show({
        message: extractErrorMessage(error, t('arc_error_removing_item')),
        color: 'red',
        icon: <IconX size={18} />
      })
    } finally {
      setIsRemoving(false)
    }
  }, [projectId, formData.skillsAndStandards, updateField, t])

  const handleRemoveItem = useCallback(async (id: string) => {
    if (!projectId || isChecking) return

    setIsChecking(true)
    try {
      const checkResult = await checkItemRemoval(projectId, id)

      if (checkResult.activity_count === 0) {
        await executeCurrentRemovalFlow(id)
      } else {
        const activityLabel = checkResult.activity_count === 1
          ? t('arc_word_activity')
          : t('arc_word_activities')
        const message = checkResult.has_assessments
          ? t('arc_confirm_removal_with_assessments', {
            activityCount: checkResult.activity_count,
            activityLabel,
            studentCount: checkResult.affected_students_count,
            studentLabel: checkResult.affected_students_count === 1
              ? t('arc_word_student')
              : t('arc_word_students')
          })
          : t('arc_confirm_removal_with_activities', {
            activityCount: checkResult.activity_count,
            activityLabel
          })
        setConfirmModal({
          opened: true,
          itemId: id,
          message
        })
      }
    } catch (error: unknown) {
      notifications.show({
        message: extractErrorMessage(error, t('arc_error_removing_item')),
        color: 'red',
        icon: <IconX size={18} />
      })
    } finally {
      setIsChecking(false)
    }
  }, [projectId, isChecking, executeCurrentRemovalFlow, t])

  const handleConfirmRemoval = useCallback(() => {
    if (confirmModal.itemId) {
      executeConfirmedRemoval(confirmModal.itemId)
    }
  }, [confirmModal.itemId, executeConfirmedRemoval])

  const handleCloseConfirmModal = useCallback(() => {
    if (!isRemoving) {
      setConfirmModal({ opened: false, itemId: '', message: '' })
    }
  }, [isRemoving])

  const isProcessing = isChecking || isRemoving

  const handleAddItems = useCallback(async (items: SelectedSkillStandard[]) => {
    if (!projectId) return

    setIsSaving(true)

    try {
      // Adicionar items imutavelmente (remove duplicatas automaticamente)
      const allItems = addSkillsStandards(formData.skillsAndStandards, items)

      // Calcular quantos items novos foram adicionados
      const addedCount = allItems.length - formData.skillsAndStandards.length

      if (addedCount === 0) {
        closeModal()
        setIsSaving(false)
        return
      }

      // Salvar na API
      const itemIds = extractItemIds(allItems)
      const response = await updateSelectedSkillsStandards(projectId, { lv4_item_ids: itemIds })

      // Verificar se há erro na resposta (igual ao NewProject)
      if (response?.errors && response.errors !== '') {
        // Verificar se é o erro específico de item em uso
        const errorMessage = response.errors.includes('Activities are currently utilizing') ||
                            response.errors.includes('utilizing a specific item')
          ? t('arc_item_in_use')
          : response.errors

        notifications.show({
          message: errorMessage,
          color: 'red',
          icon: <IconX size={18} />
        })
        setIsSaving(false)
        return
      }

      // Update estado
      updateField('skillsAndStandards', allItems)

      notifications.show({
        message: t('arc_items_added', { count: addedCount }),
        color: 'green',
        icon: <IconCheck size={18} />
      })

      closeModal()
    } catch (error: any) {
      console.error('Error adding items:', error)

      // Extrair mensagem de erro do backend
      const errorMessage = error?.response?.data?.errors ||
                          error?.response?.data?.error ||
                          error?.message ||
                          t('arc_error_adding_items')

      notifications.show({
        message: errorMessage,
        color: 'red',
        icon: <IconX size={18} />
      })
    } finally {
      setIsSaving(false)
    }
  }, [projectId, formData.skillsAndStandards, updateField, closeModal, t])

  // Mostrar skeleton enquanto carrega curriculum
  if (isLoadingCurriculum) {
    return (
      <Stack spacing="md">
        {/* Header */}
        <div>
          <Skeleton height={28} width={250} mb="xs" />
          <Skeleton height={18} width={400} />
        </div>

        <Divider />

        {/* Skills list header */}
        <Group position="apart">
          <Skeleton height={20} width={100} />
          <Skeleton height={36} width={140} radius="md" />
        </Group>

        {/* Skill cards skeleton */}
        <Stack spacing="sm">
          {[1, 2, 3].map((i) => (
            <Paper key={i} p="md" radius="md" withBorder>
              <Group position="apart">
                <Stack spacing="xs" style={{ flex: 1 }}>
                  <Group spacing="xs">
                    <Skeleton height={24} width={24} radius="xl" />
                    <Skeleton height={20} width={200} />
                  </Group>
                  <Skeleton height={16} width={300} />
                </Stack>
                <Skeleton height={32} width={32} radius="md" />
              </Group>
            </Paper>
          ))}
        </Stack>
      </Stack>
    )
  }

  // Mostrar erro se não tiver projectId nem gradeLevel
  if (!projectId && !formData.gradeLevel) {
    return (
      <Center py="xl">
        <Text color="red">{t('arc_no_project_id')}</Text>
      </Center>
    )
  }

  return (
    <>
      <Stack spacing="md">
        <div>
          <Title order={2}>{t('arc_step_standards_skills')}</Title>
          <Text color="dimmed">{t('arc_step_standards_skills_description')}</Text>
        </div>

        <Divider />

        <AddedSkillsList
          items={formData.skillsAndStandards}
          onRemove={handleRemoveItem}
          onAddClick={openModal}
          showCyclesBadges={false}
          disabled={isProcessing}
        />
      </Stack>

      <AddSkillsModal
        opened={modalOpened}
        onClose={closeModal}
        selectedItems={formData.skillsAndStandards}
        onAddItems={handleAddItems}
        skillsStandards={skillsStandards}
        isLoading={isSaving}
      />

      <ConfirmRemovalModal
        opened={confirmModal.opened}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmRemoval}
        message={confirmModal.message}
        isLoading={isRemoving}
      />
    </>
  )
}
